/**
 * @file Registre centralisé des chemins de routes du frontend.
 * @module packages.common.routes.paths
 *
 * Source unique de vérité pour toutes les URL internes, partagée par les apps
 * dashboard, public et widgets. Regroupe les chemins par surface : `auth`,
 * `app` (dashboard établissement), `admin` (back-office Brydge) et `company`
 * (pages publiques d'un établissement, adressées par `:slug`). Les entrées sont
 * soit des chaînes constantes, soit des fonctions prenant les paramètres dynamiques
 * (id, slug, token…) et renvoyant le chemin résolu. Certaines URL de `settings`
 * portent des query params (`?section=...&tab=...`) ciblant un onglet précis.
 */

const ROOTS = {
  AUTH: '/auth',
  APP: '/dashboard',
  ADMIN: '/admin',
  // Racine vide : les pages publiques d'établissement sont adressées par slug
  // à la racine du domaine (ex. /:slug), sans préfixe de surface.
  COMPANY: '',
};

export const paths = {
  siteURL: 'https://brydge.app/',

  // AUTH
  auth: {
    login: `${ROOTS.AUTH}/login`,
    register: `${ROOTS.AUTH}/register`,
    verifyEmail: `${ROOTS.AUTH}/verify-email/:token`,
    requestVerification: `${ROOTS.AUTH}/request-verification`,
    requestResetPassword: `${ROOTS.AUTH}/request-reset-password`,
    resetPassword: `${ROOTS.AUTH}/reset-password/:token`,
    twoFactor: `${ROOTS.AUTH}/two-factor`,
  },

  // DASHBOARD USER
  app: {
    root: ROOTS.APP,
    dashboard: `${ROOTS.APP}`,
    userAccount: `${ROOTS.APP}/user-account`,
    settings: {
      root: `${ROOTS.APP}/settings`,
      // Section Établissement
      company: {
        informations: `${ROOTS.APP}/settings?section=company&tab=informations`,
        identity: `${ROOTS.APP}/settings?section=company&tab=identity`,
        socialNetworks: `${ROOTS.APP}/settings?section=company&tab=social-networks`,
        stripe: `${ROOTS.APP}/settings?section=company&tab=stripe`,
      },
      // Section Paiements
      payment: {
        invoices: `${ROOTS.APP}/settings?section=payment&tab=invoices-brydge`,
        modules: `${ROOTS.APP}/settings?section=payment&tab=modules`,
      },
      // Section Bons cadeaux
      giftVouchers: {
        appearance: `${ROOTS.APP}/settings?section=giftVouchers&tab=appearance`,
        salePage: `${ROOTS.APP}/settings?section=giftVouchers&tab=sale-page`,
        saleOptions: `${ROOTS.APP}/settings?section=giftVouchers&tab=sale-options`,
        commission: `${ROOTS.APP}/settings?section=giftVouchers&tab=commission`,
        cgv: `${ROOTS.APP}/settings?section=giftVouchers&tab=cgv`,
      },
      // Section Utilisateurs
      users: {
        list: `${ROOTS.APP}/settings?section=users&tab=list`,
      },
      // Section Notifications
      notifications: {
        configuration: `${ROOTS.APP}/settings?section=notifications&tab=configuration`,
      },
      // Section Système
      system: {
        integration: `${ROOTS.APP}/settings?section=system&tab=integration`,
      },
      // Section Restaurant
      restaurant: {
        configuration: `${ROOTS.APP}/settings?section=restaurant&tab=configuration`,
        seating: `${ROOTS.APP}/settings?section=restaurant&tab=seating`,
        slots: `${ROOTS.APP}/settings?section=restaurant&tab=slots`,
        exceptions: `${ROOTS.APP}/settings?section=restaurant&tab=exceptions`,
      },
      // Section Réservations
      reservations: {
        formulas: `${ROOTS.APP}/settings?section=reservations&tab=formulas`,
        cardHold: `${ROOTS.APP}/settings?section=reservations&tab=card-hold`,
        waitlist: `${ROOTS.APP}/settings?section=reservations&tab=waitlist`,
        messaging: `${ROOTS.APP}/settings?section=reservations&tab=messaging`,
        reviews: `${ROOTS.APP}/settings?section=reservations&tab=reviews`,
      },
      // Section Wellness (le catalogue porte un onglet interne via `catalogTab`)
      wellness: {
        root: `${ROOTS.APP}/settings?section=wellness`,
        spaces: `${ROOTS.APP}/settings?section=wellness&tab=spaces`,
        catalog: `${ROOTS.APP}/settings?section=wellness&tab=catalog`,
        catalogServices: `${ROOTS.APP}/settings?section=wellness&tab=catalog&catalogTab=services`,
        catalogExtras: `${ROOTS.APP}/settings?section=wellness&tab=catalog&catalogTab=extras`,
        catalogProducts: `${ROOTS.APP}/settings?section=wellness&tab=catalog&catalogTab=products`,
        employees: `${ROOTS.APP}/settings?section=wellness&tab=employees`,
        // Pages plein écran de création/édition du catalogue, routes sœurs de
        // settings (même pattern que l'éditeur de plan de salle restaurant)
        catalogServicesCreate: `${ROOTS.APP}/settings/wellness/catalog/services/create`,
        catalogServicesEdit: (id) => `${ROOTS.APP}/settings/wellness/catalog/services/edit/${id}`,
        catalogExtrasCreate: `${ROOTS.APP}/settings/wellness/catalog/extras/create`,
        catalogExtrasEdit: (id) => `${ROOTS.APP}/settings/wellness/catalog/extras/edit/${id}`,
        catalogProductsCreate: `${ROOTS.APP}/settings/wellness/catalog/products/create`,
        catalogProductsEdit: (id) => `${ROOTS.APP}/settings/wellness/catalog/products/edit/${id}`,
      },
    },
    giftVoucher: {
      root: `${ROOTS.APP}/gift-voucher`,
      catalog: {
        list: `${ROOTS.APP}/gift-voucher/catalog`,
        create: `${ROOTS.APP}/gift-voucher/catalog/create`,
        createValue: `${ROOTS.APP}/gift-voucher/catalog/create/value`,
        createService: `${ROOTS.APP}/gift-voucher/catalog/create/service`,
        edit: (type, id) => `${ROOTS.APP}/gift-voucher/catalog/edit/${type}/${id}`,
      },
      promotions: {
        list: `${ROOTS.APP}/gift-voucher/promotions`,
        create: `${ROOTS.APP}/gift-voucher/promotions/create`,
        edit: (id) => `${ROOTS.APP}/gift-voucher/promotions/edit/${id}`,
      },
      manage: `${ROOTS.APP}/gift-voucher/manage`,
      statistics: `${ROOTS.APP}/gift-voucher/statistics`,
      details: (id) => `${ROOTS.APP}/gift-voucher/details/${id}`,
    },
    newsletter: {
      root: `${ROOTS.APP}/newsletter`,
      list: `${ROOTS.APP}/newsletter`,
      edit: (id) => `${ROOTS.APP}/newsletter/${id}`,
      configure: (id) => `${ROOTS.APP}/newsletter/${id}/configuration`,
    },
    customers: {
      root: `${ROOTS.APP}/customers`,
      list: `${ROOTS.APP}/customers`,
      create: `${ROOTS.APP}/customers/create`,
      edit: (id) => `${ROOTS.APP}/customers/edit/${id}`,
      details: (id) => `${ROOTS.APP}/customers/details/${id}`,
      duplicates: `${ROOTS.APP}/customers/duplicates`,
    },
    wellness: {
      root: `${ROOTS.APP}/wellness`,
      // Les listes et le catalogue (y compris création/édition) vivent dans
      // les paramètres : voir paths.app.settings.wellness.
      calendar: {
        root: `${ROOTS.APP}/wellness/calendar`,
      },
      bookings: {
        root: `${ROOTS.APP}/wellness/bookings`,
        list: `${ROOTS.APP}/wellness/bookings`,
      },
      pos: {
        root: `${ROOTS.APP}/wellness/pos`,
        sale: `${ROOTS.APP}/wellness/pos/sale`,
        closing: `${ROOTS.APP}/wellness/pos/closing`,
        closingWithDate: (date) => `${ROOTS.APP}/wellness/pos/closing?date=${date}`,
        reports: `${ROOTS.APP}/wellness/pos/reports`,
        payment: `${ROOTS.APP}/wellness/pos/payment`,
      },
    },
    restaurant: {
      root: `${ROOTS.APP}/restaurant`,
      seatingEditor: (areaId) => `${ROOTS.APP}/settings/seating/${areaId}`,
      // Tri global de l'ordre d'attribution des tables, toutes salles confondues.
      // Route soeur de l'editeur de plan (drill-in depuis la vue des cards).
      seatingPriorities: `${ROOTS.APP}/settings/seating-priorities`,
      // Ordre d'affichage des tables dans les selecteurs de saisie backoffice,
      // par salle. Independant de la priorite d'attribution automatique.
      seatingDisplayOrder: `${ROOTS.APP}/settings/table-display-order`,
      reservations: `${ROOTS.APP}/restaurant/reservations`,
      waitlist: `${ROOTS.APP}/restaurant/waitlist`,
      messages: `${ROOTS.APP}/restaurant/messages`,
      // Discussion d'une réservation ouverte dans la messagerie (lien des notifications et des emails)
      conversation: (reservationId) => `${ROOTS.APP}/restaurant/messages?reservation=${reservationId}`,
      reviews: `${ROOTS.APP}/restaurant/reviews`,
      statistics: `${ROOTS.APP}/restaurant/statistics`,
    },
    // Page transverse accessible depuis tout module utilisant les SMS
    smsWallet: `${ROOTS.APP}/sms-wallet`,
  },

  // DASHBOARD ADMIN
  admin: {
    root: ROOTS.ADMIN,
    dashboard: `${ROOTS.ADMIN}/dashboard`,

    // Établissements
    merchants: {
      root: `${ROOTS.ADMIN}/merchants`,
      accounts: `${ROOTS.ADMIN}/merchants/accounts`,
      accountDetails: (id) => `${ROOTS.ADMIN}/merchants/accounts/${id}`,
      companies: `${ROOTS.ADMIN}/merchants/companies`,
      createCompany: `${ROOTS.ADMIN}/merchants/companies/create`,
      companyDetails: (id) => `${ROOTS.ADMIN}/merchants/companies/${id}`,
      subscriptions: `${ROOTS.ADMIN}/merchants/subscriptions`,
    },

    // Analytics
    analytics: `${ROOTS.ADMIN}/analytics`,

    // Finances
    finances: {
      root: `${ROOTS.ADMIN}/finances`,
      invoices: `${ROOTS.ADMIN}/finances/invoices`,
      commissions: `${ROOTS.ADMIN}/finances/commissions`,
    },

    // CRM
    crm: `${ROOTS.ADMIN}/crm`,

    // Communications
    communications: {
      root: `${ROOTS.ADMIN}/communications`,
      emails: `${ROOTS.ADMIN}/communications/emails`,
      newsletterQuotas: `${ROOTS.ADMIN}/communications/newsletter-quotas`,
      sms: `${ROOTS.ADMIN}/communications/sms`,
    },

    // Équipe (gestion des collaborateurs du panel — super admin)
    team: `${ROOTS.ADMIN}/team`,

    // Réglages plateforme (interrupteur emails/SMS dev/staging — super admin)
    settings: `${ROOTS.ADMIN}/settings`,
  },

  // COMPANY
  company: {
    root: ROOTS.COMPANY,
    home: `${ROOTS.COMPANY}/:slug`, // Page d'accueil de l'établissement
    giftVoucherList: `${ROOTS.COMPANY}/:slug/gift-vouchers`, // Liste des bons cadeaux
    purchase: `${ROOTS.COMPANY}/:slug/buy/:voucherId`, // Page d'achat de bon cadeau
    createCustomVoucher: `${ROOTS.COMPANY}/:slug/create-custom-voucher`, // Page création bon sur-mesure
    paymentResult: `${ROOTS.COMPANY}/:slug/payment-result/:voucherId`,
    confirmation: `${ROOTS.COMPANY}/:slug/confirmation/:orderId`, // Page de confirmation d'un achat
    // Réservation de table (parcours public par slug, sans clé API)
    reservation: `${ROOTS.COMPANY}/:slug/reservation`,
    reservationConfirmed: `${ROOTS.COMPANY}/:slug/reservation-confirmed`, // Confirmation de réservation
    reservationManage: `${ROOTS.COMPANY}/:slug/reservation/manage`, // Gestion d'une réservation
    reservationPayment: `${ROOTS.COMPANY}/:slug/reservation/payment`, // Mise à jour de paiement (lien email)
    reservationCardHold: `${ROOTS.COMPANY}/:slug/reservation/card-hold-request`, // Demande d'empreinte bancaire (lien email)
    reservationTerms: `${ROOTS.COMPANY}/:slug/reservation/terms`, // CGV réservations
    terms: `${ROOTS.COMPANY}/:slug/terms`, // Page de CGV
    privacyPolicy: `${ROOTS.COMPANY}/:slug/privacy-policy`, // Politique de confidentialité
    review: `${ROOTS.COMPANY}/:slug/review/:token`, // Page d'avis client
    contact: `${ROOTS.COMPANY}/:slug/contact`, // Page de contact
    newsletterUnsubscribe: `${ROOTS.COMPANY}/:slug/newsletter/unsubscribe`, // Désinscription newsletter (lien email)
  },
};
